import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import slotService, { Slot, SlotCreationData, BulkSlotCreationData, SlotUpdateData, SlotAvailability } from '../services/slotService';

interface SlotState {
  slots: Slot[];
  availability: SlotAvailability[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  selectedGymId: number | null;
}

const initialState: SlotState = {
  slots: [],
  availability: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  selectedGymId: null
};

/**
 * Async thunk for creating single slot
 */
export const createSlot = createAsyncThunk(
  'slot/create',
  async ({ gymId, data }: { gymId: number; data: SlotCreationData }, { rejectWithValue }) => {
    try {
      const response = await slotService.createSlot(gymId, data);
      return response.slot;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create slot');
    }
  }
);

/**
 * Async thunk for creating bulk slots
 */
export const createBulkSlots = createAsyncThunk(
  'slot/createBulk',
  async ({ gymId, data }: { gymId: number; data: BulkSlotCreationData }, { rejectWithValue }) => {
    try {
      const response = await slotService.createBulkSlots(gymId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create slots');
    }
  }
);

/**
 * Async thunk for fetching gym slots
 */
export const fetchGymSlots = createAsyncThunk(
  'slot/fetchAll',
  async (
    {
      gymId,
      filters
    }: {
      gymId: number;
      filters?: {
        date?: string;
        date_from?: string;
        date_to?: string;
        is_active?: boolean;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await slotService.getGymSlots(gymId, filters);
      return { ...response, gymId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch slots');
    }
  }
);

/**
 * Async thunk for updating slot
 */
export const updateSlot = createAsyncThunk(
  'slot/update',
  async ({ gymId, slotId, data }: { gymId: number; slotId: number; data: SlotUpdateData }, { rejectWithValue }) => {
    try {
      const response = await slotService.updateSlot(gymId, slotId, data);
      return response.slot;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update slot');
    }
  }
);

/**
 * Async thunk for deleting slot
 */
export const deleteSlot = createAsyncThunk(
  'slot/delete',
  async ({ gymId, slotId }: { gymId: number; slotId: number }, { rejectWithValue }) => {
    try {
      await slotService.deleteSlot(gymId, slotId);
      return slotId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete slot');
    }
  }
);

/**
 * Async thunk for fetching slot availability
 */
export const fetchSlotAvailability = createAsyncThunk(
  'slot/fetchAvailability',
  async ({ gymId, date }: { gymId: number; date: string }, { rejectWithValue }) => {
    try {
      const response = await slotService.getSlotAvailability(gymId, date);
      return response.slots;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch availability');
    }
  }
);

const slotSlice = createSlice({
  name: 'slot',
  initialState,
  reducers: {
    setSelectedGymId: (state, action) => {
      state.selectedGymId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Create Slot
    builder
      .addCase(createSlot.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSlot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.slots.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createSlot.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create Bulk Slots
    builder
      .addCase(createBulkSlots.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBulkSlots.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalCount += action.payload.slots_created;
      })
      .addCase(createBulkSlots.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Slots
    builder
      .addCase(fetchGymSlots.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGymSlots.fulfilled, (state, action) => {
        state.isLoading = false;
        state.slots = action.payload.slots;
        state.totalCount = action.payload.total_count;
        state.selectedGymId = action.payload.gymId;
      })
      .addCase(fetchGymSlots.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Slot
    builder
      .addCase(updateSlot.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSlot.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.slots.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.slots[index] = action.payload;
        }
      })
      .addCase(updateSlot.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Slot
    builder
      .addCase(deleteSlot.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSlot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.slots = state.slots.filter((s) => s.id !== action.payload);
        state.totalCount -= 1;
      })
      .addCase(deleteSlot.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Availability
    builder
      .addCase(fetchSlotAvailability.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSlotAvailability.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availability = action.payload;
      })
      .addCase(fetchSlotAvailability.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setSelectedGymId, clearError } = slotSlice.actions;
export default slotSlice.reducer;
